import { Box, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { FiChevronDown, FiChevronLeft } from 'react-icons/fi';
import { CiSearch } from 'react-icons/ci';
import useStyles from './index.styles';

import { kollectAITheme } from 'config/theme';
import { useAppDispatch } from 'hooks/useRedux';
import { getWorkflowExecutionStats } from 'store/reducer/workflowReducer/action';
import useClickOutside from 'hooks/useClickOutside';

interface WorflowFilterType {
  label: string;
  placeholder: string;
  name: string;
}
interface TimeFilterType {
  label: string;
  days?: number;
}

const workflowFilters = [
  { label: 'name', name: 'name', placeholder: 'Name of the workflow' },
  { label: 'id', name: 'execution_id', placeholder: 'Execution ID' },
  { label: 'Executed by', name: 'executed_by', placeholder: 'Executed by' },
];

const timeFilters = [
  { label: 'Today', days: 0 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  // { label: 'Custom', value: '', days: null },
];

const WorkflowExecutionFilters = ({
  setLoading,
}: {
  setLoading: (loading: boolean) => void;
}) => {
  const classes = useStyles();
  const filterRef = useRef<HTMLDivElement>(null);
  const timeRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const [selectedWorkflowFilter, setSelectedWorkflowFilter] =
    useState<WorflowFilterType>(workflowFilters[0]);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState<TimeFilterType>(
    timeFilters[1]
  );
  const [searchValue, setSearchValue] = useState('');
  const [showFilterOption, setShowFilterOption] = useState(false);
  const [showTimeOption, setShowTimeOption] = useState(false);

  const onWorkflowFilterClick = () => {
    setSearchValue('');
    setShowFilterOption(!showFilterOption);
  };

  const onTimeFilterClick = () => {
    setShowTimeOption((prev) => !prev);
  };

  const handleSearch = async (
    workflow?: WorflowFilterType,
    isEmpty?: boolean
  ) => {
    try {
      setLoading(true);
      const field = workflow ? workflow.name : selectedWorkflowFilter?.name;
      const value = isEmpty ? '' : searchValue;
      if (isEmpty) {
        await dispatch(
          getWorkflowExecutionStats({ days: selectedTimeFilter.days })
        );
      } else {
        await dispatch(
          getWorkflowExecutionStats({
            days: selectedTimeFilter.days,
            [field]: value,
          })
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleTimeFilter = async (filter: TimeFilterType) => {
    try {
      setLoading(true);
      let payload: any = { days: filter.days };
      if (searchValue) payload[selectedWorkflowFilter.name] = searchValue;
      await dispatch(getWorkflowExecutionStats(payload));
    } finally {
      setLoading(false);
    }
  };

  useClickOutside(
    filterRef,
    () => showFilterOption && setShowFilterOption(false)
  );
  useClickOutside(timeRef, () => showTimeOption && setShowTimeOption(false));

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <Box className={classes.container}>
          <button
            onClick={onTimeFilterClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <Box className={classes.searchLabelText}>
              <Typography>{selectedTimeFilter.label}</Typography>
            </Box>
            {showTimeOption ? (
              <FiChevronLeft fontSize={20} />
            ) : (
              <FiChevronDown fontSize={20} />
            )}
          </button>
        </Box>

        {showTimeOption && (
          <Box ref={timeRef} className={classes.dropdownContainer}>
            {timeFilters.map((option: TimeFilterType, index: number) => {
              return (
                <Box
                  key={index}
                  className={classes.dropdownMenu}
                  onClick={() => {
                    setSelectedTimeFilter(option);
                    setShowTimeOption(false);
                    handleTimeFilter(option);
                  }}
                >
                  <Typography className={classes.dropdownMenuLabel}>
                    {`${option.label}:`}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={{ position: 'relative' }}>
        <Box className={classes.container}>
          <Typography
            className={classes.searchLabelText}
            onClick={onWorkflowFilterClick}
          >
            {`${selectedWorkflowFilter.label}:`}
          </Typography>

          <input
            type='text'
            placeholder={`${selectedWorkflowFilter.placeholder}`}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              if (!e.target.value) {
                handleSearch(selectedWorkflowFilter, true);
              }
            }}
            className={classes.searchInput}
            onKeyDown={onKeyDown}
          />

          <button onClick={onWorkflowFilterClick}>
            {showFilterOption ? (
              <FiChevronLeft fontSize={20} />
            ) : (
              <FiChevronDown fontSize={20} />
            )}
          </button>

          <button onClick={() => handleSearch()}>
            <CiSearch
              color={kollectAITheme.primaryColor}
              fontWeight={600}
              fontSize={20}
            />
          </button>

          {/* <button disabled={clearDisabled} onClick={onClearFilter}>
            <Typography className={classes.clearFilter}>
              Clear filter
            </Typography>
          </button> */}
        </Box>

        {showFilterOption && (
          <Box ref={filterRef} className={classes.dropdownContainer}>
            <Typography sx={{ marginBottom: '0.8rem' }}>
              SEARCH USING
            </Typography>
            {workflowFilters.map((option: WorflowFilterType, index: number) => {
              const { label, placeholder } = option;
              return (
                <Box
                  key={index}
                  className={classes.dropdownMenu}
                  onClick={() => {
                    setSelectedWorkflowFilter(option);
                    setShowFilterOption(false);
                    handleSearch(option);
                  }}
                >
                  <Typography className={classes.dropdownMenuLabel}>
                    {`${label}:`}
                  </Typography>
                  <Typography color={'#8593B5'}>{placeholder}</Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WorkflowExecutionFilters;
